import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import api from '../../lib/api';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface PracticeGoal {
  id: string;
  label: string;
  description: string;
}

const emptyForm = () => ({ label: '', description: '' });

export function AdminGoalsPage() {
  const [goals, setGoals] = useState<PracticeGoal[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [goalIdToDelete, setGoalIdToDelete] = useState<string | null>(null);

  useEffect(() => {
    loadGoals();
  }, []);

  const loadGoals = () => {
    api
      .get('/admin/goals')
      .then((res) => setGoals(res.data))
      .catch(() => {});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.patch(`/admin/goals/${editingId}`, {
          label: form.label.trim(),
          description: form.description.trim(),
        });
      } else {
        await api.post('/admin/goals', {
          label: form.label.trim(),
          description: form.description.trim(),
        });
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      loadGoals();
    } catch (err) {
      console.error('Failed to save goal', err);
      Sentry.captureException(err);
    }
  };

  const handleEdit = (g: PracticeGoal) => {
    setForm({ label: g.label, description: g.description || '' });
    setEditingId(g.id);
    setShowForm(true);
  };

  const confirmDelete = async () => {
    if (!goalIdToDelete) return;
    const id = goalIdToDelete;
    setGoalIdToDelete(null);
    try {
      await api.delete(`/admin/goals/${id}`);
      loadGoals();
    } catch (err) {
      console.error('Failed to delete goal', err);
      Sentry.captureException(err);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Practice goals catalog</h1>
          <p className="text-gray-600 mt-1">
            Platform-wide goals organizations can enable for their teams. Tenants can also add their
            own org-specific goals.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setShowForm(true);
            setEditingId(null);
            setForm(emptyForm());
          }}
          className="bg-red-700 text-white px-4 py-2 rounded-md font-medium hover:bg-red-800"
        >
          New goal
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg border mb-8">
          <h2 className="text-lg font-semibold mb-4">{editingId ? 'Edit' : 'Create'} goal</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="admin-goal-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Label
              </label>
              <input
                id="admin-goal-label"
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                required
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label
                htmlFor="admin-goal-desc"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <textarea
                id="admin-goal-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-800"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(emptyForm());
                }}
                className="border border-gray-300 px-4 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg border divide-y">
        {goals.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No platform goals yet.</div>
        ) : (
          goals.map((g) => (
            <div key={g.id} className="p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 break-words">{g.label}</div>
                {g.description ? (
                  <div className="text-sm text-gray-600 mt-1 break-words">{g.description}</div>
                ) : null}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => handleEdit(g)}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setGoalIdToDelete(g.id)}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={goalIdToDelete !== null}
        title="Delete platform goal?"
        description="Delete this platform goal? Organizations using it will unlink."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setGoalIdToDelete(null)}
      />
    </div>
  );
}
